import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import ChatInterface from './ChatInterface';

const ChatScreen = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conversation, setConversation] = useState<any>(null);
  const [advisor, setAdvisor] = useState<any>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!conversationId) {
      navigate('/advisors');
      return;
    }

    const fetchConversation = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch the conversation details
        const { data, error: fetchError } = await supabase
          .from('conversations')
          .select(`
            id,
            client_id,
            advisor_id,
            status,
            advisor:advisor_id (
              display_name,
              profile_image_url,
              per_message_rate
            )
          `)
          .eq('id', conversationId)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          throw new Error('Conversation not found');
        }

        // Check if the user is part of this conversation
        if (data.client_id !== user.id) {
          throw new Error('You do not have permission to view this conversation');
        }

        // Check if the conversation is active
        if (data.status !== 'active') {
          throw new Error('This conversation is no longer active');
        }

        setConversation(data);
        setAdvisor({
          id: data.advisor_id,
          name: data.advisor.display_name,
          image: data.advisor.profile_image_url || 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=400&h=250',
          status: 'busy',
          per_message_rate: data.advisor.per_message_rate || 0
        });
      } catch (err) {
        console.error('Error fetching conversation:', err);
        setError(err instanceof Error ? err.message : 'Failed to load conversation');
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
  }, [user, conversationId, navigate]);

  const handleChatClose = () => {
    navigate('/client/dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your conversation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex items-center text-red-600 mb-4">
            <AlertCircle className="w-6 h-6 mr-2" />
            <h2 className="text-xl font-semibold">Error</h2>
          </div>
          <p className="text-gray-700 mb-4">{error}</p>
          <button
            onClick={() => navigate('/advisors')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors w-full"
          >
            Browse Advisors
          </button>
        </div>
      </div>
    );
  }

  if (!conversation || !advisor) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
          <p className="text-gray-600">Conversation not found or no longer active.</p>
          <button
            onClick={() => navigate('/advisors')}
            className="mt-4 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Browse Advisors
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full h-[80vh] flex flex-col">
        <div className="flex-1 overflow-hidden">
          <ChatInterface
            advisor={advisor}
            conversationId={conversation.id}
            onClose={handleChatClose}
            isClient={true}
          />
        </div>
      </div>
    </div>
  );
};

export default ChatScreen;